import React, { Component, SyntheticEvent } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { delay } from "@formant/core/common/delay";
import { logger } from "@formant/core/common/logger";
import { duration } from "@formant/core/model/duration";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";
import { Measure } from "@formant/ui/component/common/Measure";

import * as styles from "./AssetImage.scss";

export interface IAssetImageProps {
    src?: string;
    className?: string;
    onImageSize?: (width: number, height: number) => void;
}

@observer
export class AssetImage extends Component<IAssetImageProps> {
    @observable
    private loaded: boolean = false;
    @observable
    private error: boolean = false;
    @observable
    private imgAspect = 1;
    @observable
    private containerAspect = 1;
    @observable
    private src?: string;
    private nextSrc?: string;
    private loadingSrc?: string;
    private image = new Image();

    public render() {
        const { className, src } = this.props;

        const loading = this.src === undefined && this.nextSrc !== undefined;

        const fill = Math.abs(this.imgAspect / this.containerAspect - 1) < 0.1;

        this.load(src);

        return (
            <Measure
                onMeasureResize={this.onResizeContainer}
                className={classnames(
                    styles["asset-image"],
                    className,
                    this.loaded && src && styles.loaded,
                    this.error && styles.error,
                    fill && styles.fill,
                )}
            >
                {this.src && (
                    <img
                        src={this.src}
                        onLoad={this.onLoad}
                        onError={this.onError}
                    />
                )}
                <LoadingIndicator visible={loading} />
            </Measure>
        );
    }

    private load(src: string | undefined) {
        this.nextSrc = src;
        if (!this.loadingSrc && src) {
            this.loadingSrc = src;
            this.image.src = src;
            this.image.onload = () => {
                this.loadingSrc = undefined;
                this.src = this.image.src;
                if (this.nextSrc) {
                    this.image.src = this.nextSrc;
                    this.nextSrc = undefined;
                }
            };
            this.image.onerror = () => {
                this.error = true;
                logger.warn("Asset load failure", {
                    src,
                });
            };
        }
    }

    private onResizeContainer = (width: number, height: number) => {
        this.containerAspect = width / (height + 1);
    };

    private onLoad = (event: SyntheticEvent<HTMLImageElement>) => {
        const { onImageSize } = this.props;
        const { naturalWidth, naturalHeight } = event.currentTarget;

        if (onImageSize) {
            onImageSize(naturalWidth, naturalHeight);
        }

        this.imgAspect = naturalWidth / (naturalHeight + 1);
        this.error = false;
        this.loaded = true;
    };

    private onError = async (event: SyntheticEvent<HTMLImageElement>) => {
        this.loaded = false;
        this.error = true;
        const element = event.currentTarget;
        const assetUrl = element.src;
        logger.warn("Asset load failure", {
            assetUrl,
        });
        await delay(duration.second);
        this.error = false;
        element.setAttribute("src", assetUrl);
    };
}
